export const BASE_URL = (function () {
    if (process.env.REACT_APP_XDOME_BASE_URL) {
        return process.env.REACT_APP_XDOME_BASE_URL;
    }

    if (process.env.NODE_ENV === 'development') {
        return "http://localhost:8080/xdome";
    }

    // return "http://xdome-staging.eba-jmbgm3yi.us-east-2.elasticbeanstalk.com/xdome";

})();

export const SERVER_INFO_URL = (function () {
    if (process.env.REACT_APP_SERVER_INFO_URL) {
        return process.env.REACT_APP_SERVER_INFO_URL;
    }

    if (BASE_URL) {
        return `${BASE_URL}/api/management/info`;
    }

    return "/xdome/api/management/info";
})();

export const APP_RELEASE_STAGE = (function () {
    if (process.env.REACT_APP_RELEASE_STAGE) {
        return process.env.REACT_APP_RELEASE_STAGE;
    }

    return "development";
})();

export const APP_VERSION_NUMBER = (() => {
    const version = process.env.REACT_APP_VERSION ?? "0.0.0";
    const branch = process.env.REACT_APP_GIT_BRANCH ?? "BRANCH";
    const buildNumber = process.env.REACT_APP_BUILD_NUMBER;
    let appNumber = process.env.REACT_APP_ENVIRONMENT === "prod" ?
        `${version}:${branch}` :
        `${version}:${branch}-${buildNumber}`

    return appNumber;
})();

export const BUGSNAG_API_KEY = (function () {
    if (process.env.REACT_APP_BUGSNAG_API_KEY) {
        return process.env.REACT_APP_BUGSNAG_API_KEY;
    }
    return "29934dc0b9c81b087b00f884e45d5dd9";
})();

export const TOKEN_KEY = "ACCESS_TOKEN";
export const PROFILE_KEY = "PROFILE";

export const LOGOUT_DELAY_MS = 2000;
export const ERROR_NOTIFICATION_DURATION_MS = 5000;
export const WARNING_NOTIFICATION_DURATION_MS = 4000;
export const INFO_NOTIFICATION_DURATION_MS = 3000;
export const SUCCESS_NOTIFICATION_DURATION_MS = 3000;
export const SNACKBAR_AUTO_HIDE_DURATION_MS = 3000;
export const SNACKBAR_LONG_AUTO_HIDE_DURATION_MS = 6000;
export const NOTIFICATION_CLOSE_ANIMATION_DELAY_MS = 300;
export const TEXT_EDITOR_INITIALIZATION_DELAY_MS = 1000;
export const TABLE_SETTINGS_DIALOG_DELAY_MS = 100;

export interface BaseAxiosResponse { }

interface Headers {
    [key: string]: string;
}

export function getHeaders() {
    let headers: Headers = {};
    if (localStorage.getItem(TOKEN_KEY) && localStorage.getItem(TOKEN_KEY) !== null) {
        headers['X-Auth-Token'] = localStorage.getItem(TOKEN_KEY)!;
    }
    headers['Content-Type'] = 'application/json';
    return headers;
}

interface ParsedAlignment {
    vertical: string;
    horizontal: string;
}

function cleanAlignmentString(alignment: string): string {
    return alignment
        .replace(/bottomp/gi, 'bottom')
        .replace(/topp/gi, 'top')
        .replace(/middlee/gi, 'middle')
        .replace(/centerr/gi, 'center')
        .replace(/leftt/gi, 'left')
        .replace(/rightt/gi, 'right');
}

function parseAlignment(alignment: string): ParsedAlignment {
    let clean = cleanAlignmentString(alignment);
    const validPattern = /^(top|middle|bottom)-(left|center|right)$/;
    
    if (validPattern.test(clean)) {
        const [vertical, horizontal] = clean.split('-');
        return { vertical: vertical || 'top', horizontal: horizontal || 'left' };
    }
    
    const matches = clean.match(/(top|middle|bottom)-(left|center|right)/g);
    if (matches && matches.length > 0) {
        const [vertical, horizontal] = matches[matches.length - 1].split('-');
        return { vertical: vertical || 'top', horizontal: horizontal || 'left' };
    }
    
    const valignMatch = clean.match(/(top|middle|bottom)/i);
    const alignMatch = clean.match(/(left|center|right)/i);
    
    return {
        vertical: valignMatch ? valignMatch[0].toLowerCase() : 'top',
        horizontal: alignMatch ? alignMatch[0].toLowerCase() : 'left'
    };
}

function getCellAlignment(cell: HTMLElement, p: HTMLElement | null): ParsedAlignment {
    if (p) {
        const dataCellAlign = p.getAttribute('data-cell-align');
        if (dataCellAlign) {
            return parseAlignment(dataCellAlign);
        }
        
        const dataAlign = p.getAttribute('data-align');
        if (dataAlign) {
            const alignMatch = dataAlign.match(/(left|center|right)/i);
            if (alignMatch) {
                const styleVerticalAlign = (cell.style.verticalAlign || '').trim() || 'top';
                return { vertical: styleVerticalAlign, horizontal: alignMatch[0].toLowerCase() };
            }
        }
        
        const pStyle = p.getAttribute('style') || '';
        const textAlignMatch = pStyle.match(/text-align\s*:\s*([^;]+)/i);
        if (textAlignMatch) {
            const horizontal = textAlignMatch[1].trim().toLowerCase();
            if (['left', 'center', 'right'].includes(horizontal)) {
                const styleVerticalAlign = (cell.style.verticalAlign || '').trim() || 'top';
                return { vertical: styleVerticalAlign, horizontal };
            }
        }
    }
    
    const cellAlign = cell.getAttribute('align')?.toLowerCase();
    const styleVerticalAlign = (cell.style.verticalAlign || '').trim() || 'top';
    return {
        vertical: styleVerticalAlign,
        horizontal: cellAlign && ['left', 'center', 'right'].includes(cellAlign) ? cellAlign : 'left'
    };
}

function rebuildCellStyleAttribute(cell: HTMLElement, verticalAlign: string): void {
    const styles: string[] = [];
    for (let i = 0; i < cell.style.length; i++) {
        const prop = cell.style[i];
        const value = cell.style.getPropertyValue(prop);
        if (value && prop !== 'vertical-align') {
            styles.push(`${prop}: ${value}`);
        }
    }
    styles.push(`vertical-align: ${verticalAlign}`);
    cell.setAttribute('style', styles.join('; '));
}

function applyTextAlignToParagraph(p: HTMLElement, align: string): void {
    const existingStyle = p.getAttribute('style') || '';
    const styleWithoutTextAlign = existingStyle.replace(/text-align\s*:\s*[^;]+;?/gi, '').trim();
    const newStyle = styleWithoutTextAlign 
        ? `${styleWithoutTextAlign}; text-align: ${align}`.replace(/;+/g, ';').trim()
        : `text-align: ${align}`;
    p.setAttribute('style', newStyle);
    p.style.textAlign = align;
}

export function normalizeTableAlignment(html: string, externalAlignment = false): string {
    const template = document.createElement('template');
    template.innerHTML = html;

    const content = template.content;

    const tables = content.querySelectorAll('table');

    tables.forEach((table) => {
        const rows = Array.from(table.querySelectorAll('tr'));
        rows.forEach((row) => {
            const cells = row.querySelectorAll('td, th');

            if (cells.length === 0) {
                row.remove();
                return;
            }
        });

        const lastRow = table.querySelector('tr:last-child');
        let columnCount = 0;

        if (lastRow) {
            const windowWidth = window.innerWidth;
            columnCount = lastRow.querySelectorAll('td, th').length;

            lastRow.querySelectorAll('td').forEach((td) => {
                if (columnCount > 0 && !td.style.width) {
                    const colspan = td.getAttribute('colspan');
                    if (!colspan) {
                        const widthPx = (windowWidth / columnCount) - 45;
                        const widthPt = Math.round(widthPx * 0.75);
                        td.style.width = `${widthPt}pt`;
                    }
                }
            });
        }

        if (externalAlignment) {
            table.querySelectorAll('tr[valign]').forEach((tr) => {
                const valign = tr.getAttribute('valign');
                if (valign) {
                    tr.removeAttribute('valign');
                    tr.querySelectorAll('td, th').forEach((cell) => {
                        (cell as HTMLElement).style.verticalAlign = valign;
                    });
                }
            });

            table.querySelectorAll('td, th').forEach((cell) => {
                const cellElement = cell as HTMLElement;
                let p = cell.querySelector('p') as HTMLElement | null;
                
                const alignment = getCellAlignment(cellElement, p);
                const { vertical: valign, horizontal: align } = alignment;

                cell.removeAttribute('valign');
                cell.removeAttribute('align');
                
                const classAttr = cell.getAttribute('class');
                if (classAttr && classAttr.includes('style=')) {
                    cell.removeAttribute('class');
                }
                
                rebuildCellStyleAttribute(cellElement, valign);

                if (!p) {
                    p = document.createElement('p');
                    while (cell.firstChild) {
                        p.appendChild(cell.firstChild);
                    }
                    cell.appendChild(p);
                }
                
                p.setAttribute('data-cell-align', `${valign}-${align}`);
                applyTextAlignToParagraph(p, align);
            });
        }
    });

    return template.innerHTML;
}

export function convertTableDivsToP(html: string): string {
    const template = document.createElement('template');
    template.innerHTML = html;

    const content = template.content;
    const tables = content.querySelectorAll('table');

    tables.forEach((table) => {
        table.querySelectorAll('td, th').forEach((cell) => {
            const divs = cell.querySelectorAll('div');
            divs.forEach((div) => {
                const p = document.createElement('p');
                Array.from(div.attributes).forEach((attr) => {
                    p.setAttribute(attr.name, attr.value);
                });
                if (div.hasAttribute('style')) {
                    p.setAttribute('style', div.getAttribute('style') || '');
                }
                while (div.firstChild) {
                    p.appendChild(div.firstChild);
                }
                div.parentNode?.replaceChild(p, div);
            });
        });
    });

    return template.innerHTML;
}

export function addColGroupsToTables(html: string): string {
    const template = document.createElement('template');
    template.innerHTML = html;

    const content = template.content;
    const tables = content.querySelectorAll('table');

    tables.forEach((table) => {
        const existingColgroup = table.querySelector('colgroup');
        if (existingColgroup) {
            existingColgroup.remove();
        }
        const firstRow = table.querySelector('tr');
        if (!firstRow) return;

        const cells = firstRow.querySelectorAll('td, th');
        if (cells.length === 0) return;

        let columnCount = 0;
        const cellSpans: number[] = [];
        cells.forEach((cell) => {
            const colspan = parseInt(cell.getAttribute('colspan') || '1');
            cellSpans.push(colspan);
            columnCount += colspan;
        });

        if (columnCount === 0) return;

        const existingColgroupForSync = table.querySelector('colgroup');
        if (existingColgroupForSync) {
            const cols = existingColgroupForSync.querySelectorAll('col');
            if (cols.length === cells.length) {
                const rows = table.querySelectorAll('tr');
                rows.forEach((row) => {
                    const rowCells = row.querySelectorAll('td, th');
                    rowCells.forEach((cell, index) => {
                        if (cols[index]) {
                            const colStyle = cols[index].getAttribute('style') || '';
                            const widthMatch = colStyle.match(/width:\s*(\d+)pt/);
                            if (widthMatch) {
                                const width = widthMatch[1];
                                const cellStyle = (cell as HTMLElement).getAttribute('style') || '';
                                if (!cellStyle.includes('width:')) {
                                    const newStyle = cellStyle ? `${cellStyle}; width: ${width}pt;` : `width: ${width}pt;`;
                                    (cell as HTMLElement).setAttribute('style', newStyle.replace(/;+/g, ';').trim());
                                }
                            }
                        }
                    });
                });
            }
        }

        const isLandscape = table.hasAttribute('data-print-landscape');
        const defaultWidth = isLandscape ? 612 : 366;

        let tableWidth = defaultWidth;
        const tableStyle = table.getAttribute('style') || '';
        const widthMatch = tableStyle.match(/width:\s*(\d+)pt/);
        if (widthMatch) {
            tableWidth = parseInt(widthMatch[1]);
        }

        let hasCustomWidths = false;
        let customWidths: number[] = [];
        if (existingColgroup) {
            const existingCols = existingColgroup.querySelectorAll('col');
            if (existingCols.length === cellSpans.length) {
                const widths = Array.from(existingCols).map(col => {
                    const style = col.getAttribute('style') || '';
                    const match = style.match(/width:\s*(\d+)pt/);
                    return match ? parseInt(match[1]) : 0;
                });

                const uniformWidth = Math.floor(tableWidth / columnCount);
                hasCustomWidths = widths.some(width => Math.abs(width - uniformWidth) > 5);
                if (hasCustomWidths) {
                    customWidths = widths;
                }
            }
        }

        if (!hasCustomWidths) {
            const actualCellWidths: number[] = [];
            const rows = table.querySelectorAll('tr');

            rows.forEach((row) => {
                const rowCells = row.querySelectorAll('td, th');
                rowCells.forEach((cell, cellIndex) => {
                    const style = (cell as HTMLElement).getAttribute('style') || '';
                    const widthMatch = style.match(/width:\s*(\d+(?:\.\d+)?)(pt|px)/);
                    if (widthMatch) {
                        let width = parseFloat(widthMatch[1]);
                        const unit = widthMatch[2];
                        if (unit === 'px') {
                            width = Math.round(width * 0.75);
                        }
                        if (width > 1) {
                            if (!actualCellWidths[cellIndex] || actualCellWidths[cellIndex] === 0) {
                                actualCellWidths[cellIndex] = Math.round(width);
                            }
                        }
                    }
                });
            });

            if (actualCellWidths.length > 0 && actualCellWidths.some(w => w > 0)) {
                const uniformWidth = Math.floor(tableWidth / columnCount);
                const nonZeroWidths = actualCellWidths.filter(w => w > 0);
                const hasNonUniformCells = nonZeroWidths.some(width => Math.abs(width - uniformWidth) > 5);
                const hasVariedWidths = nonZeroWidths.length > 1 &&
                    nonZeroWidths.some(w => Math.abs(w - nonZeroWidths[0]) > 5);

                if (hasNonUniformCells || hasVariedWidths) {
                    hasCustomWidths = true;
                    customWidths = cellSpans.map((span, index) => {
                        return actualCellWidths[index] > 0 ? actualCellWidths[index] : uniformWidth * span;
                    });
                }
            }
        }

        const singleColumnWidth = Math.floor(tableWidth / columnCount);
        const colgroup = document.createElement('colgroup');

        if (hasCustomWidths) {
            const totalCustomWidth = customWidths.reduce((sum, width) => sum + width, 0);
            if (totalCustomWidth > tableWidth * 1.1) {
                const scale = tableWidth / totalCustomWidth;
                customWidths = customWidths.map(width => Math.round(width * scale));
            }

            cellSpans.forEach((span, index) => {
                const col = document.createElement('col');
                const colWidth = customWidths[index] || singleColumnWidth * span;
                col.setAttribute('style', `width: ${colWidth}pt;`);
                if (span > 1) {
                    col.setAttribute('span', span.toString());
                }
                colgroup.appendChild(col);
            });

            const rows = table.querySelectorAll('tr');
            rows.forEach((row) => {
                const rowCells = row.querySelectorAll('td, th');
                rowCells.forEach((cell, cellIndex) => {
                    if (customWidths[cellIndex]) {
                        const existingStyle = (cell as HTMLElement).getAttribute('style') || '';
                        const styleWithoutWidth = existingStyle.replace(/width:\s*\d+(?:\.\d+)?(?:pt|px);?/g, '').trim();
                        const newStyle = styleWithoutWidth ? `${styleWithoutWidth}; width: ${customWidths[cellIndex]}pt;` : `width: ${customWidths[cellIndex]}pt;`;
                        (cell as HTMLElement).setAttribute('style', newStyle.replace(/;+/g, ';').replace(/^;|;$/g, '').trim());
                    }
                });
            });
        } else {
            cellSpans.forEach((span) => {
                const col = document.createElement('col');
                const colWidth = singleColumnWidth * span;
                col.setAttribute('style', `width: ${colWidth}pt;`);
                if (span > 1) {
                    col.setAttribute('span', span.toString());
                }
                colgroup.appendChild(col);
            });
        }

        if (table.firstChild) {
            table.insertBefore(colgroup, table.firstChild);
        } else {
            table.appendChild(colgroup);
        }
    });

    return template.innerHTML;
}

const convertCellDimensionsToPoints = (table: HTMLTableElement) => {
    const cells = table.querySelectorAll<HTMLTableCellElement>('td, th');
    cells.forEach((cell) => {
        const currentWidth = cell.style.width;
        if (currentWidth && currentWidth.includes('px')) {
            const widthPx = parseInt(currentWidth.replace('px', ''));
            const widthPt = Math.round(widthPx * 0.75);
            cell.style.width = `${widthPt}pt`;
        }

        const currentHeight = cell.style.height;
        if (currentHeight && currentHeight.includes('px')) {
            const heightPx = parseInt(currentHeight.replace('px', ''));
            const heightPt = Math.round(heightPx * 0.75);
            cell.style.height = `${heightPt}pt`;
        }
    });
};

export const applyTableResize = (onContentChange?: () => void) => {
    const tables = document.querySelectorAll('.editor-dialog .sun-editor-editable table');

    if (tables.length === 0) return;

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                const target = mutation.target as HTMLElement;
                if (target.tagName === 'TD' || target.tagName === 'TH') {
                    const currentWidth = target.style.width;
                    if (currentWidth && currentWidth.includes('px')) {
                        const widthPx = parseInt(currentWidth.replace('px', ''));
                        const widthPt = Math.round(widthPx * 0.75);
                        target.style.width = `${widthPt}pt`;
                    }

                    const currentHeight = target.style.height;
                    if (currentHeight && currentHeight.includes('px')) {
                        const heightPx = parseInt(currentHeight.replace('px', ''));
                        const heightPt = Math.round(heightPx * 0.75);
                        target.style.height = `${heightPt}pt`;
                    }
                }
            }
        });
    });

    tables.forEach((table) => {
        convertCellDimensionsToPoints(table as HTMLTableElement);
        observer.observe(table, {
            attributes: true,
            attributeFilter: ['style'],
            subtree: true
        });

        const resizableColumns = table.querySelectorAll<HTMLTableCellElement>('th, td');
        let startX: number;
        let startWidth: number;

        resizableColumns.forEach((column) => {
            column.addEventListener('mousemove', function (event: MouseEvent) {
                const rect = this.getBoundingClientRect();
                const offset = 16;
                if (event.pageX > rect.right - offset) {
                    this.style.cursor = 'col-resize';
                } else {
                    this.style.cursor = 'auto';
                }
            });

            column.addEventListener('mousedown', function (event: MouseEvent) {
                const rect = this.getBoundingClientRect();
                const offset = 16;
                if (event.pageX > rect.right - offset) {
                    startX = event.pageX;
                    startWidth = this.offsetWidth;

                    const columnToResize = this;
                    const table = columnToResize.closest('table');
                    if (!table || !columnToResize.parentElement) return;

                    const columnIndex = Array.from(columnToResize.parentElement.children).indexOf(columnToResize);

                    const cellResize = (e: MouseEvent) => {
                        const newWidthPx = startWidth + (e.pageX - startX);
                        const newWidthPt = Math.round(newWidthPx * 0.75);
                        columnToResize.style.width = `${newWidthPt}pt`;
                        const rows = table.rows;

                        for (let i = 0; i < rows.length; i++) {
                            const cell = rows[i].cells[columnIndex];
                            if (!cell) continue;

                            if (i === rows.length - 1 && rows.length > 1) {
                                cell.style.width = '1px';
                            } else {
                                cell.style.width = `${newWidthPt}pt`;
                            }
                        }

                        const colgroup = table.querySelector('colgroup');
                        if (colgroup) {
                            const firstRow = table.querySelector('tr');
                            if (firstRow) {
                                const cells = firstRow.querySelectorAll('td, th');
                                const cols = colgroup.querySelectorAll('col');

                                cells.forEach((cell, index) => {
                                    if (cols[index]) {
                                        const cellWidth = (cell as HTMLElement).style.width;
                                        if (cellWidth && cellWidth.includes('pt')) {
                                            const widthValue = cellWidth.replace('pt', '');
                                            cols[index].setAttribute('style', `width: ${widthValue}pt;`);
                                        }
                                    }
                                });
                            }
                        }
                    };

                    const stopResize = () => {
                        document.removeEventListener('mousemove', cellResize);
                        document.removeEventListener('mouseup', stopResize);

                        const colgroup = table.querySelector('colgroup');
                        if (colgroup) {
                            const firstRow = table.querySelector('tr');
                            if (firstRow) {
                                const cells = firstRow.querySelectorAll('td, th');
                                const cols = colgroup.querySelectorAll('col');

                                cells.forEach((cell, index) => {
                                    if (cols[index]) {
                                        const cellWidth = (cell as HTMLElement).style.width;
                                        if (cellWidth && cellWidth.includes('pt')) {
                                            const widthValue = cellWidth.replace('pt', '');
                                            cols[index].setAttribute('style', `width: ${widthValue}pt;`);
                                        }
                                    }
                                });
                            }
                        }

                        if (onContentChange) {
                            onContentChange();
                        }
                    };

                    document.addEventListener('mousemove', cellResize);
                    document.addEventListener('mouseup', stopResize);
                }
            });
        });
    });
};
